import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ScanLine, Link2, RotateCcw, CheckCircle2, AlertTriangle, Package, Truck, MapPinned } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { docProtocol, fmtDate, StatusChip } from "../lib/format";
import { classifyScanCode, playScanBeep } from "../lib/barcode";
import BarcodeScanner from "../components/BarcodeScanner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";

const LOG_KEY = "nafe_bipagem_log";
const DELIVERER_KEY = "nafe_bipagem_deliverer";
const SECTOR_KEY = "nafe_bipagem_sector";
const UNASSIGNED = "__none__";

function loadLog() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(LOG_KEY) || "[]");
    return Array.isArray(raw) ? raw.slice(0, 30) : [];
  } catch {
    return [];
  }
}

function saveLog(entries) {
  sessionStorage.setItem(LOG_KEY, JSON.stringify(entries.slice(0, 30)));
}

function loadSessionDeliverer() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(DELIVERER_KEY) || "null");
    if (raw && typeof raw.id === "string") return raw;
  } catch {
    /* ignore */
  }
  return null;
}

function saveSessionDeliverer(value) {
  if (!value) sessionStorage.removeItem(DELIVERER_KEY);
  else sessionStorage.setItem(DELIVERER_KEY, JSON.stringify(value));
}

function loadSessionSector() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(SECTOR_KEY) || "null");
    if (raw && typeof raw.id === "string" && typeof raw.name === "string") return raw;
  } catch {
    /* ignore */
  }
  return null;
}

function saveSessionSector(value) {
  if (!value) sessionStorage.removeItem(SECTOR_KEY);
  else sessionStorage.setItem(SECTOR_KEY, JSON.stringify(value));
}

function sectorLabel(sector, tenantNames) {
  const office = tenantNames[sector.tenant_id];
  return office ? `${office} — ${sector.name}` : sector.name;
}

function DocPreview({ doc, t }) {
  if (!doc) return null;
  return (
    <div className="grid sm:grid-cols-2 gap-3 text-sm" data-testid="bipagem-preview">
      <div>
        <div className="text-[11px] uppercase tracking-wide text-[var(--text-3)]">Protocolo</div>
        <div className="font-mono font-semibold text-[var(--text-1)]">{docProtocol(doc)}</div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-[var(--text-3)]">Status</div>
        <StatusChip status={doc.status} t={t} />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-[var(--text-3)]">{t("label_recipient")}</div>
        <div className="text-[var(--text-1)]">{doc.recipient_name || "—"}</div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-[var(--text-3)]">Cidade</div>
        <div className="text-[var(--text-1)]">{[doc.city, doc.state].filter(Boolean).join("/") || "—"}</div>
      </div>
      <div className="sm:col-span-2">
        <div className="text-[11px] uppercase tracking-wide text-[var(--text-3)]">{t("label_address")}</div>
        <div className="text-[var(--text-1)]">{doc.address || "—"}</div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-[var(--text-3)]">Código de barras</div>
        <div className="font-mono text-[var(--text-1)]">{doc.barcode || "—"}</div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-[var(--text-3)]">{t("label_notary")}</div>
        <div className="text-[var(--text-1)]">{doc.notary_office || "—"}</div>
      </div>
      {doc.sector_name && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-[var(--text-3)]">{t("nav_sectors")}</div>
          <div className="text-[var(--text-1)]">{doc.sector_name}</div>
        </div>
      )}
    </div>
  );
}

export default function Bipagem() {
  const { t } = useI18n();
  const [barcode, setBarcode] = useState("");
  const [protocol, setProtocol] = useState("");
  const [doc, setDoc] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState("Bipe o código de barras do AR ou o número de protocolo.");
  const [conflict, setConflict] = useState(null);
  const [deliverers, setDeliverers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [tenantNames, setTenantNames] = useState({});
  const [listsReady, setListsReady] = useState(false);
  const [sessionDeliverer, setSessionDeliverer] = useState(loadSessionDeliverer);
  const [sessionSector, setSessionSector] = useState(loadSessionSector);
  const [log, setLog] = useState(loadLog);
  const [selectedLogIds, setSelectedLogIds] = useState([]);
  const [fixing, setFixing] = useState(false);
  const [fixDelivererId, setFixDelivererId] = useState("");
  const pairRef = useRef({ barcode: "", protocol: "" });
  const sessionDelivererRef = useRef(sessionDeliverer);
  const sessionSectorRef = useRef(sessionSector);

  useEffect(() => {
    sessionDelivererRef.current = sessionDeliverer;
  }, [sessionDeliverer]);

  useEffect(() => {
    sessionSectorRef.current = sessionSector;
  }, [sessionSector]);

  useEffect(() => {
    Promise.all([
      api.get("/users?role=deliverer").catch(() => ({ data: [] })),
      api.get("/sectors").catch(() => ({ data: [] })),
      api.get("/tenants").catch(() => ({ data: [] })),
    ]).then(([userRes, sectorRes, tenantRes]) => {
      setDeliverers(userRes.data || []);
      setSectors(sectorRes.data || []);
      setTenantNames(Object.fromEntries((tenantRes.data || []).map((tenant) => [tenant.id, tenant.name])));
      setListsReady(true);
    });
  }, []);

  const resetPair = useCallback(() => {
    pairRef.current = { barcode: "", protocol: "" };
    setBarcode("");
    setProtocol("");
    setDoc(null);
    setConflict(null);
    setHint("Bipe o código de barras do AR ou o número de protocolo.");
  }, []);

  const pushLog = useCallback((entry) => {
    setLog((prev) => {
      const next = [{ id: `${Date.now()}-${Math.random()}`, at: new Date().toISOString(), ...entry }, ...prev].slice(0, 30);
      saveLog(next);
      return next;
    });
  }, []);

  const lookup = async (code) => {
    const { data } = await api.get("/barcode/lookup", { params: { code } });
    return data;
  };

  const link = async ({ barcode: bc, protocol_number, force = false }) => {
    const { data } = await api.post("/barcode/link", {
      barcode: bc,
      protocol_number,
      force,
    });
    return data;
  };

  const persistSessionDeliverer = (value) => {
    sessionDelivererRef.current = value;
    setSessionDeliverer(value);
    saveSessionDeliverer(value);
  };

  const persistSessionSector = (value) => {
    sessionSectorRef.current = value;
    setSessionSector(value);
    saveSessionSector(value);
  };

  const orderedSectors = useMemo(() => (
    [...sectors].sort((a, b) => sectorLabel(a, tenantNames).localeCompare(sectorLabel(b, tenantNames), "pt-BR"))
  ), [sectors, tenantNames]);

  const visibleDeliverers = useMemo(() => {
    if (!sessionSector?.id) return deliverers;
    const sector = sectors.find((item) => item.id === sessionSector.id);
    if (!sector) return deliverers;
    const allowed = new Set((sector.deliverers || []).map((deliverer) => deliverer.id));
    return deliverers.filter((deliverer) => allowed.has(deliverer.id));
  }, [deliverers, sectors, sessionSector]);

  useEffect(() => {
    if (!listsReady || !sessionSector?.id) return;
    if (!sectors.some((sector) => sector.id === sessionSector.id)) persistSessionSector(null);
  }, [listsReady, sectors, sessionSector]);

  useEffect(() => {
    if (!listsReady || !sessionDeliverer?.id) return;
    if (!visibleDeliverers.some((deliverer) => deliverer.id === sessionDeliverer.id)) {
      persistSessionDeliverer(null);
    }
  }, [listsReady, visibleDeliverers, sessionDeliverer]);

  useEffect(() => {
    if (!fixDelivererId) return;
    if (!visibleDeliverers.some((deliverer) => deliverer.id === fixDelivererId)) setFixDelivererId("");
  }, [visibleDeliverers, fixDelivererId]);

  const onSectorChange = (value) => {
    if (value === UNASSIGNED) {
      persistSessionSector(null);
      setHint("Bipe o código de barras do AR ou o número de protocolo.");
      return;
    }
    const sector = sectors.find((item) => item.id === value);
    if (!sector) return;
    const next = { id: sector.id, name: sectorLabel(sector, tenantNames) };
    persistSessionSector(next);
    const allowed = new Set((sector.deliverers || []).map((deliverer) => deliverer.id));
    if (sessionDelivererRef.current?.id && !allowed.has(sessionDelivererRef.current.id)) {
      persistSessionDeliverer(null);
    }
    toast.success(`${t("bipagem_now_sector")} ${next.name}`);
  };

  const onDelivererChange = (value) => {
    if (value === UNASSIGNED) {
      persistSessionDeliverer(null);
      setHint("Bipe o código de barras do AR ou o número de protocolo.");
      return;
    }
    const deliverer = deliverers.find((d) => d.id === value);
    if (!deliverer) return;
    persistSessionDeliverer({ id: deliverer.id, name: deliverer.name });
    toast.success(`${t("bipagem_now_scanning")} ${deliverer.name}`);
    setHint(`Entregador: ${deliverer.name}. Bipe o código de barras ou o protocolo.`);
  };

  const reassignDocuments = async (documentIds, deliverer) => {
    const ids = [...new Set((documentIds || []).filter(Boolean))];
    if (!ids.length || !deliverer?.id) return 0;
    try {
      const { data } = await api.post("/documents/assign-deliverer", {
        document_ids: ids,
        deliverer_id: deliverer.id,
      });
      return data?.updated ?? ids.length;
    } catch {
      let updated = 0;
      for (const id of ids) {
        await api.patch(`/documents/${id}`, { deliverer_id: deliverer.id });
        updated += 1;
      }
      return updated;
    }
  };

  const applyDelivererToLog = (documentIds, deliverer) => {
    const ids = new Set(documentIds);
    setLog((prev) => {
      const next = prev.map((row) => (
        ids.has(row.documentId)
          ? { ...row, delivererId: deliverer.id, delivererName: deliverer.name }
          : row
      ));
      saveLog(next);
      return next;
    });
    setLastDoc((prev) => (
      prev && ids.has(prev.id)
        ? { ...prev, deliverer_id: deliverer.id, deliverer_name: deliverer.name }
        : prev
    ));
  };

  const onFixRowDeliverer = async (row, delivererId) => {
    const deliverer = deliverers.find((d) => d.id === delivererId);
    if (!deliverer || !row.documentId) return;
    setFixing(true);
    try {
      await reassignDocuments([row.documentId], deliverer);
      applyDelivererToLog([row.documentId], deliverer);
      toast.success(`${t("bipagem_fix_ok")}: ${row.protocol || ""} · ${deliverer.name}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("deliverer_assign_fail"));
    } finally {
      setFixing(false);
    }
  };

  const onFixSelected = async () => {
    const deliverer = deliverers.find((d) => d.id === fixDelivererId);
    const ids = log.filter((row) => selectedLogIds.includes(row.id) && row.documentId).map((row) => row.documentId);
    if (!deliverer) {
      toast.error(t("so_deliverer_select"));
      return;
    }
    if (!ids.length) {
      toast.error(t("bipagem_fix_none"));
      return;
    }
    setFixing(true);
    try {
      const updated = await reassignDocuments(ids, deliverer);
      applyDelivererToLog(ids, deliverer);
      setSelectedLogIds([]);
      setFixDelivererId("");
      toast.success(`${t("bipagem_fix_ok")}: ${updated} · ${deliverer.name}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("deliverer_assign_fail"));
    } finally {
      setFixing(false);
    }
  };

  const toggleLogRow = (rowId) => {
    setSelectedLogIds((prev) => (prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]));
  };

  const assignDocToSession = async (linkedDoc) => {
    const current = sessionDelivererRef.current;
    const sector = sessionSectorRef.current;
    if (!linkedDoc?.id) return linkedDoc;

    const patch = {};
    if (sector?.id && linkedDoc.sector_id !== sector.id) patch.sector_id = sector.id;
    if (current?.id && linkedDoc.deliverer_id !== current.id) patch.deliverer_id = current.id;
    else if (!current?.id && linkedDoc.status === "received") patch.status = "sorting";

    if (!Object.keys(patch).length) {
      return {
        ...linkedDoc,
        deliverer_name: current?.name || linkedDoc.deliverer_name,
        sector_name: sector?.name || linkedDoc.sector_name,
      };
    }

    try {
      const { data } = await api.patch(`/documents/${linkedDoc.id}`, patch);
      return {
        ...data,
        deliverer_name: current?.name || linkedDoc.deliverer_name,
        sector_name: sector?.name || linkedDoc.sector_name,
      };
    } catch (e) {
      const failedDeliverer = Boolean(patch.deliverer_id);
      toast.error(formatApiError(e?.response?.data?.detail) || t(failedDeliverer ? "deliverer_assign_fail" : "doc_update_failed"));
      return linkedDoc;
    }
  };

  const finishSuccess = async (linkedDoc, bc, already) => {
    const assigned = await assignDocToSession(linkedDoc);
    playScanBeep(true);
    setLastDoc(assigned);
    setDoc(null);
    const protocolLabel = docProtocol(assigned);
    const sessionName = sessionDelivererRef.current?.name;
    pushLog({
      ok: true,
      already: !!already,
      documentId: assigned?.id,
      barcode: bc || assigned?.barcode,
      protocol: protocolLabel,
      recipient: assigned?.recipient_name,
      delivererId: sessionDelivererRef.current?.id || assigned?.deliverer_id || null,
      delivererName: assigned?.deliverer_name || sessionName || null,
      sectorName: sessionSectorRef.current?.name || assigned?.sector_name || null,
    });
    toast.success(already ? `Já vinculado: ${protocolLabel}` : `Vinculado: ${protocolLabel}`);
    pairRef.current = { barcode: "", protocol: "" };
    setBarcode("");
    setProtocol("");
    setConflict(null);
    setHint(
      sessionName
        ? `Vinculado a ${sessionName}. Bipe o próximo.`
        : already ? "Código já estava no sistema. Bipe o próximo AR." : "Vínculo gravado. Bipe o próximo AR.",
    );
  };

  const handleConflict = (err, bc, protocolNumber) => {
    playScanBeep(false);
    const data = err?.response?.data || {};
    setConflict({
      reason: data.reason,
      detail: formatApiError(data.detail),
      existing: data.existing,
      barcode: bc,
      protocol: protocolNumber,
    });
  };

  const confirmForce = async () => {
    if (!conflict) return;
    setBusy(true);
    try {
      const data = await link({
        barcode: conflict.barcode,
        protocol_number: conflict.protocol,
        force: true,
      });
      setConflict(null);
      await finishSuccess(data.document, data.barcode, data.already_linked);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const tryLink = async (bc, protocolNumber) => {
    setBusy(true);
    try {
      const data = await link({ barcode: bc, protocol_number: protocolNumber });
      await finishSuccess(data.document, data.barcode, data.already_linked);
    } catch (e) {
      if (e?.response?.status === 409) handleConflict(e, bc, protocolNumber);
      else {
        playScanBeep(false);
        const msg = formatApiError(e?.response?.data?.detail);
        toast.error(msg);
        setHint(msg);
        if (e?.response?.status === 404) {
          pairRef.current.protocol = "";
          setProtocol("");
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const onScan = async (raw) => {
    if (busy) return;
    const { kind, value } = classifyScanCode(raw);
    if (!kind) return;

    const isNotFound = (e) => e?.response?.status === 404;

    if (kind === "barcode") {
      pairRef.current.barcode = value;
      setBarcode(value);
      if (pairRef.current.protocol) {
        await tryLink(value, pairRef.current.protocol);
        return;
      }
      setBusy(true);
      try {
        const found = await lookup(value);
        if (found?.barcode && String(found.barcode).toUpperCase() === value.toUpperCase()) {
          await finishSuccess(found, value, true);
        } else if (found) {
          setDoc(found);
          const label = docProtocol(found);
          setHint(
            label
              ? `Documento ${label} encontrado. Bipe o protocolo para vincular o código.`
              : "Documento encontrado. Bipe o número de protocolo.",
          );
        } else {
          setDoc(null);
          setHint("Código de barras lido. Agora bipe o número de protocolo.");
          toast.message("Aguardando protocolo");
        }
      } catch (e) {
        if (!isNotFound(e)) {
          playScanBeep(false);
          toast.error(formatApiError(e?.response?.data?.detail));
          return;
        }
        setDoc(null);
        setHint("Código de barras lido. Agora bipe o número de protocolo.");
        toast.message("Aguardando protocolo");
      } finally {
        setBusy(false);
      }
      return;
    }

    setProtocol(value);
    if (pairRef.current.barcode) {
      pairRef.current.protocol = value;
      await tryLink(pairRef.current.barcode, value);
      return;
    }
    setBusy(true);
    try {
      const found = await lookup(value);
      pairRef.current.protocol = value;
      setDoc(found);
      if (found?.barcode) {
        await finishSuccess(found, found.barcode, true);
        return;
      }
      setHint("Protocolo encontrado. Agora bipe o código de barras do AR.");
      toast.message("Aguardando código de barras");
    } catch (e) {
      playScanBeep(false);
      pairRef.current.protocol = "";
      setProtocol("");
      setDoc(null);
      const msg = isNotFound(e) ? "Protocolo não encontrado" : formatApiError(e?.response?.data?.detail);
      toast.error(msg);
      setHint("Protocolo não encontrado. Confira o número e bipe de novo.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !conflict) resetPair();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [resetPair, conflict]);

  return (
    <div className="space-y-6" data-testid="bipagem-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl flex items-center gap-2">
            <ScanLine size={22} /> Bipagem de AR
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("bipagem_intro")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => resetPair()} data-testid="bipagem-reset">
            <RotateCcw size={14} className="mr-2" /> Limpar
          </Button>
        </div>
      </div>

      <Card className="p-4 space-y-3" data-testid="bipagem-session-deliverer">
        <div className="space-y-2">
          <div className="flex items-center gap-2 font-semibold text-sm">
            <MapPinned size={16} /> {t("bipagem_session_sector")}
          </div>
          <Select value={sessionSector?.id || UNASSIGNED} onValueChange={onSectorChange}>
            <SelectTrigger className="h-11" data-testid="bipagem-sector-select">
              <SelectValue placeholder={t("so_deliverer_none")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNASSIGNED}>{t("so_deliverer_none")}</SelectItem>
              {orderedSectors.map((sector) => (
                <SelectItem key={sector.id} value={sector.id}>{sectorLabel(sector, tenantNames)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 font-semibold text-sm">
          <Truck size={16} /> {t("bipagem_session_deliverer")}
        </div>
        <Select value={sessionDeliverer?.id || UNASSIGNED} onValueChange={onDelivererChange}>
          <SelectTrigger className="h-11" data-testid="bipagem-deliverer-select">
            <SelectValue placeholder={t("so_deliverer_none")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UNASSIGNED}>{t("so_deliverer_none")}</SelectItem>
            {visibleDeliverers.map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-[var(--text-3)]">
          {sessionSector
            ? t("bipagem_sector_filtered").replace("{name}", sessionSector.name)
            : t("bipagem_sector_all")}
          {sessionDeliverer ? ` ${t("bipagem_now_scanning")} ${sessionDeliverer.name}.` : ""}
        </p>
      </Card>

      <Card className="p-5 space-y-4">
        <BarcodeScanner
          autoFocus
          disabled={busy}
          placeholder="Bipe o código de barras ou o protocolo e pressione Enter"
          inputClassName="h-14 text-xl font-mono tracking-wide"
          onScan={onScan}
        />
        <p className="text-sm text-[var(--text-2)]" data-testid="bipagem-hint">{hint}</p>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className={`rounded-2xl border p-3 ${barcode ? "border-[var(--brand)] bg-[var(--brand-soft)]" : "border-[var(--border-c)]"}`}>
            <div className="text-[11px] uppercase tracking-wide text-[var(--text-3)] mb-1">1. Código de barras</div>
            <div className="font-mono font-semibold text-lg min-h-7" data-testid="bipagem-barcode">
              {barcode || "—"}
            </div>
          </div>
          <div className={`rounded-2xl border p-3 ${protocol ? "border-[var(--brand)] bg-[var(--brand-soft)]" : "border-[var(--border-c)]"}`}>
            <div className="text-[11px] uppercase tracking-wide text-[var(--text-3)] mb-1">2. Protocolo</div>
            <div className="font-mono font-semibold text-lg min-h-7" data-testid="bipagem-protocol">
              {protocol || "—"}
            </div>
          </div>
        </div>
      </Card>

      {(doc || lastDoc) && (
        <Card className="p-5 space-y-4" data-testid="bipagem-document">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 font-semibold">
              <Package size={16} /> {doc ? "Documento" : "Último vínculo"}
            </div>
            <Link
              to={`/documents?q=${encodeURIComponent(docProtocol(doc || lastDoc))}`}
              className="text-xs text-[var(--brand)] hover:underline"
            >
              Abrir na central
            </Link>
          </div>
          <DocPreview doc={doc || lastDoc} t={t} />
        </Card>
      )}

      <Card className="p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 font-semibold text-sm">
            <Link2 size={16} /> Últimas bipagens
          </div>
          <p className="text-xs text-[var(--text-3)]">{t("bipagem_fix_deliverer")}</p>
        </div>
        {selectedLogIds.length > 0 && (
          <div className="flex flex-wrap items-end gap-2 rounded-xl border border-[var(--border-c)] p-3" data-testid="bipagem-fix-bar">
            <div className="flex-1 min-w-44">
              <div className="text-[11px] text-[var(--text-3)] mb-1">{t("bipagem_fix_selected")} ({selectedLogIds.length})</div>
              <Select value={visibleDeliverers.some((d) => d.id === fixDelivererId) ? fixDelivererId : undefined} onValueChange={setFixDelivererId} disabled={fixing}>
                <SelectTrigger className="h-9" data-testid="bipagem-fix-deliverer-select">
                  <SelectValue placeholder={t("so_deliverer_select")} />
                </SelectTrigger>
                <SelectContent>
                  {visibleDeliverers.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="button" size="sm" onClick={onFixSelected} disabled={fixing || !fixDelivererId} data-testid="bipagem-fix-selected">
              {t("bipagem_fix_selected")}
            </Button>
          </div>
        )}
        {log.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum vínculo nesta sessão.</p>
        ) : (
          <div className="space-y-2" data-testid="bipagem-log">
            {log.map((row) => (
              <div key={row.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm border-b border-[var(--border-c)] pb-2 last:border-0">
                <Checkbox
                  checked={selectedLogIds.includes(row.id)}
                  onCheckedChange={() => toggleLogRow(row.id)}
                  disabled={!row.documentId || fixing}
                  aria-label={row.protocol || row.barcode}
                />
                {row.ok ? (
                  <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
                ) : (
                  <AlertTriangle size={14} className="text-amber-500 shrink-0" />
                )}
                <span className="font-mono">{row.barcode || "—"}</span>
                <span className="text-[var(--text-3)]">→</span>
                <span className="font-mono">{row.protocol || "—"}</span>
                {row.already && <span className="text-[11px] text-[var(--text-3)]">já vinculado</span>}
                {row.sectorName && <span className="text-[11px] text-[var(--text-3)]">{row.sectorName}</span>}
                <div className="ml-auto min-w-40 w-44">
                  <Select
                    value={row.delivererId || undefined}
                    onValueChange={(value) => onFixRowDeliverer(row, value)}
                    disabled={!row.documentId || fixing}
                  >
                    <SelectTrigger className="h-8 text-xs" data-testid={`bipagem-log-deliverer-${row.documentId || row.id}`}>
                      <SelectValue placeholder={t("no_deliverer")} />
                    </SelectTrigger>
                    <SelectContent>
                      {(visibleDeliverers.some((d) => d.id === row.delivererId)
                        ? visibleDeliverers
                        : [...visibleDeliverers, ...deliverers.filter((d) => d.id === row.delivererId)]
                      ).map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-[11px] text-[var(--text-3)] w-full sm:w-auto">{fmtDate(row.at)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={!!conflict} onOpenChange={(open) => !open && setConflict(null)}>
        <DialogContent data-testid="bipagem-conflict">
          <DialogHeader>
            <DialogTitle>Conflito de vínculo</DialogTitle>
            <DialogDescription>{conflict?.detail}</DialogDescription>
          </DialogHeader>
          {conflict?.existing && (
            <div className="text-sm space-y-1 rounded-xl bg-[var(--surface-2)] p-3">
              <div>Protocolo: <span className="font-mono">{conflict.existing.protocol_number || conflict.existing.number}</span></div>
              <div>Código atual: <span className="font-mono">{conflict.existing.barcode || "—"}</span></div>
              <div>Destinatário: {conflict.existing.recipient_name || "—"}</div>
            </div>
          )}
          <p className="text-sm text-[var(--text-2)]">
            {conflict?.reason === "barcode_taken"
              ? "O código de barras será removido do outro protocolo e gravado neste."
              : "O código de barras deste protocolo será substituído."}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConflict(null)}>Cancelar</Button>
            <Button type="button" onClick={confirmForce} disabled={busy} data-testid="bipagem-force">
              Substituir vínculo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
